/**
 * Migration batches (ADR 0055 amendment 2026-10-22): moving live Vodia
 * phones onto the platform without touching a desk.
 *
 * Stage → review the holds and the text every phone will receive → Point
 * (typing the batch name: one Vodia write per extension, the change-set
 * apply) → Push (check-sync; the 15-minute refresh is the backstop) →
 * watch each phone arrive at the edge and register on a platform line →
 * Roll back (typing the name again: parameter restored or cleared, phone
 * handed Vodia's own entry URL). The platform never points on its own.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  cancelMigrationBatch,
  getMigrationBatch,
  getMigrationBatches,
  getMigrationCandidates,
  getMigrationText,
  pointMigrationBatch,
  pushMigrationBatch,
  rollbackMigrationBatch,
  stageMigrationBatch,
} from "@pbxmgr/api";
import type { MigrationBatch, MigrationItem, MigrationItemState } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner, TextField } from "@pbxmgr/ui";
import type { BadgeTone } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";
import { actionsFor, nameConfirmed, progressLine } from "./migrationBatch";

const STATUS_TONE: Record<string, BadgeTone> = {
  draft: "unknown",
  pointed: "degraded",
  failed: "offline",
  rolled_back: "unknown",
  cancelled: "unknown",
};

const ITEM_TONE: Record<MigrationItemState, BadgeTone> = {
  planned: "unknown",
  held: "degraded",
  pointed: "degraded",
  point_failed: "offline",
  pushed: "degraded",
  push_failed: "offline",
  arrived: "degraded",
  registered: "online",
  rolling_back: "degraded",
  rolled_back: "unknown",
};

function errorText(err: unknown): string {
  return err instanceof ApiError ? (err.detail ?? err.message) : "request failed";
}

function when(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function ItemRow({ item }: { item: MigrationItem }): ReactNode {
  const why = item.detail && typeof item.detail.why === "string" ? item.detail.why : null;
  return (
    <tr>
      <td>
        <code>{item.mac}</code>
        {item.label ? <div className="ui-empty">{item.label}</div> : null}
      </td>
      <td>{item.model ?? "—"}</td>
      <td>
        {item.node_name ?? "—"}
        {item.extension ? ` · ${item.extension}` : ""}
        {item.domain ? <div className="ui-empty">{item.domain}</div> : null}
      </td>
      <td>
        <Badge tone={ITEM_TONE[item.state]}>{item.state.replace("_", " ")}</Badge>
        {item.hold_reason ? <div className="ui-empty">{item.hold_reason.replace(/_/g, " ")}</div> : null}
        {why ? <div className="ui-empty">{why}</div> : null}
      </td>
      <td>{item.prior_value === null ? "none" : "set"}</td>
      <td>
        {item.registered_at
          ? `registered ${when(item.registered_at)}`
          : item.arrived_at
            ? `arrived ${when(item.arrived_at)}`
            : item.pushed_at
              ? `pushed ${when(item.pushed_at)}`
              : item.pointed_at
                ? `pointed ${when(item.pointed_at)}`
                : "—"}
        {item.rolled_back_at ? <div className="ui-empty">rolled back {when(item.rolled_back_at)}</div> : null}
      </td>
    </tr>
  );
}

function BatchDetail({ id, canManage }: { id: number; canManage: boolean }): ReactNode {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  // While phones are walking (pointed/pushed, or rolling back), the read
  // advances their states from the edge's check-ins, so poll; a batch with
  // nothing in flight is static.
  const batch = useQuery({
    queryKey: ["migration-batch", id],
    queryFn: () => getMigrationBatch(id),
    refetchInterval: (q) => {
      const data = q.state.data;
      const inFlight =
        data?.status === "pointed" ||
        data?.status === "failed" ||
        (data?.counts.rolling_back ?? 0) > 0;
      return inFlight ? 10_000 : false;
    },
  });
  const invalidate = (): void => {
    setError(null);
    setTyped("");
    void qc.invalidateQueries({ queryKey: ["migration-batch", id] });
    void qc.invalidateQueries({ queryKey: ["migration-batches"] });
    void qc.invalidateQueries({ queryKey: ["migration-candidates"] });
  };
  const onError = (err: unknown): void => setError(errorText(err));
  const point = useMutation({
    mutationFn: () => pointMigrationBatch(id),
    onSuccess: invalidate,
    onError,
  });
  const push = useMutation({
    mutationFn: () => pushMigrationBatch(id),
    onSuccess: invalidate,
    onError,
  });
  const rollback = useMutation({
    mutationFn: () => rollbackMigrationBatch(id),
    onSuccess: invalidate,
    onError,
  });
  const cancel = useMutation({
    mutationFn: () => cancelMigrationBatch(id),
    onSuccess: invalidate,
    onError,
  });

  if (batch.isLoading || !batch.data) return <Spinner />;
  const b: MigrationBatch = batch.data;
  const actions = actionsFor(b.status);
  const armed = nameConfirmed(typed, b.name);
  const busy = point.isPending || push.isPending || rollback.isPending || cancel.isPending;

  return (
    <div className="changeset-card">
      <div className="changeset-head">
        <strong>
          #{b.id} — {b.name}
        </strong>
        <Badge tone={STATUS_TONE[b.status] ?? "unknown"}>{b.status.replace("_", " ")}</Badge>
        <span className="ui-empty">{progressLine(b.counts)}</span>
      </div>
      <p className="ui-empty">
        staged {when(b.created_at)} by {b.created_by ?? "—"}
        {b.pointed_at ? ` · pointed ${when(b.pointed_at)} by ${b.pointed_by ?? "—"}` : ""}
        {b.rolled_back_at
          ? ` · rolled back ${when(b.rolled_back_at)} by ${b.rolled_back_by ?? "—"}`
          : ""}
        {b.note ? ` · ${b.note}` : ""}
      </p>
      <details>
        <summary>Text every phone receives (Vodia's snom-general for the extension)</summary>
        <pre>{b.text}</pre>
      </details>
      <table className="ui-table">
        <thead>
          <tr>
            <th>Phone</th>
            <th>Model</th>
            <th>Node · ext</th>
            <th>State</th>
            <th>Prior value</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {b.items.map((item) => (
            <ItemRow key={item.id} item={item} />
          ))}
        </tbody>
      </table>
      {canManage ? (
        <div className="pairing-actions">
          {actions.point || actions.rollback ? (
            <TextField
              id={`typed-${b.id}`}
              label={`Type the batch name to confirm: ${b.name}`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          ) : null}
          {actions.point ? (
            <Button disabled={busy || !armed} onClick={() => point.mutate()}>
              {point.isPending ? "Pointing…" : "Point (write Vodia per extension)"}
            </Button>
          ) : null}
          {actions.push ? (
            <Button disabled={busy} onClick={() => push.mutate()}>
              {push.isPending ? "Pushing…" : "Push (check-sync)"}
            </Button>
          ) : null}
          {actions.rollback ? (
            <Button variant="secondary" disabled={busy || !armed} onClick={() => rollback.mutate()}>
              {rollback.isPending ? "Rolling back…" : "Roll back to Vodia"}
            </Button>
          ) : null}
          {actions.cancel ? (
            <Button variant="secondary" disabled={busy} onClick={() => cancel.mutate()}>
              Cancel
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

export function MigrationPage(): ReactNode {
  const canManage = usePermission("migration.manage");
  const qc = useQueryClient();
  const batches = useQuery({ queryKey: ["migration-batches"], queryFn: getMigrationBatches });
  const candidates = useQuery({
    queryKey: ["migration-candidates"],
    queryFn: getMigrationCandidates,
    enabled: canManage,
  });
  const text = useQuery({ queryKey: ["migration-text"], queryFn: getMigrationText });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stage = useMutation({
    mutationFn: () => stageMigrationBatch(name.trim(), [...selected], note.trim() || undefined),
    onSuccess: (b) => {
      setError(null);
      setSelected(new Set());
      setName("");
      setNote("");
      setOpen(b.id);
      void qc.invalidateQueries({ queryKey: ["migration-batches"] });
      void qc.invalidateQueries({ queryKey: ["migration-candidates"] });
    },
    onError: (err) => setError(errorText(err)),
  });

  const toggle = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const ready = (candidates.data?.items ?? []).filter((c) => c.hold_reason === null);

  return (
    <div>
      <div className="dash__head">
        <h1>Migration</h1>
      </div>

      <Panel title="Stage a batch">
        <p>
          A batch points live Vodia phones at this platform by writing the extension's{" "}
          <code>snom-general</code> provisioning parameter on the node; Vodia pushes it on the
          next check-sync and the phone's next fetch is ours. Staging reads each extension's
          current value as the backup and writes nothing. Rollback restores it and hands the
          phone Vodia's own entry URL.
        </p>
        {text.data ? (
          <details>
            <summary>Text a batch staged now would carry</summary>
            <pre>{text.data.text}</pre>
          </details>
        ) : null}
        {!canManage ? (
          <p className="ui-empty">migration.manage required to stage or point a batch.</p>
        ) : candidates.isLoading ? (
          <Spinner />
        ) : (candidates.data?.items.length ?? 0) === 0 ? (
          <p className="ui-empty">
            No adopted phone is in a node's provisioned inventory. Import phones from Vodia
            first (Devices), then stage them here.
          </p>
        ) : (
          <>
            <table className="ui-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="select every phone that can go"
                      checked={ready.length > 0 && ready.every((c) => selected.has(c.device_id))}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked ? new Set(ready.map((c) => c.device_id)) : new Set(),
                        )
                      }
                    />
                  </th>
                  <th>Phone</th>
                  <th>Model</th>
                  <th>Node · ext</th>
                  <th>Hold</th>
                </tr>
              </thead>
              <tbody>
                {(candidates.data?.items ?? []).map((c) => (
                  <tr key={c.device_id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`select ${c.mac}`}
                        disabled={c.hold_reason !== null}
                        checked={selected.has(c.device_id)}
                        onChange={() => toggle(c.device_id)}
                      />
                    </td>
                    <td>
                      <code>{c.mac}</code>
                      {c.label ? <div className="ui-empty">{c.label}</div> : null}
                    </td>
                    <td>{c.model ?? "—"}</td>
                    <td>
                      {c.node_name ?? "—"}
                      {c.extension ? ` · ${c.extension}` : ""}
                    </td>
                    <td>
                      {c.hold_reason ? (
                        <>
                          <Badge tone="degraded">{c.hold_reason.replace(/_/g, " ")}</Badge>
                          {c.why ? <div className="ui-empty">{c.why}</div> : null}
                        </>
                      ) : (
                        <Badge tone="online">ready</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pairing-actions">
              <TextField
                id="batch-name"
                label="Batch name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                id="batch-note"
                label="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                disabled={stage.isPending || selected.size === 0 || !name.trim()}
                onClick={() => stage.mutate()}
              >
                {stage.isPending ? "Staging…" : `Stage ${selected.size} phone(s)`}
              </Button>
            </div>
          </>
        )}
        {error ? <Alert>{error}</Alert> : null}
      </Panel>

      <Panel title={`Batches (${batches.data?.total ?? 0})`}>
        {batches.isLoading ? (
          <Spinner />
        ) : (batches.data?.items.length ?? 0) === 0 ? (
          <p className="ui-empty">No batch staged yet.</p>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>Batch</th>
                <th>Status</th>
                <th>Phones</th>
                <th>Staged</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(batches.data?.items ?? []).map((b) => (
                <tr key={b.id}>
                  <td>
                    #{b.id} — {b.name}
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[b.status] ?? "unknown"}>
                      {b.status.replace("_", " ")}
                    </Badge>
                  </td>
                  <td>{progressLine(b.counts)}</td>
                  <td>{when(b.created_at)}</td>
                  <td>
                    <Button
                      variant="secondary"
                      onClick={() => setOpen(open === b.id ? null : b.id)}
                    >
                      {open === b.id ? "Close" : "Open"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {open !== null ? <BatchDetail id={open} canManage={canManage} /> : null}
      </Panel>
    </div>
  );
}
